import { useMemo, useState } from 'react';
import { Select, Table, Tag, Card, Row, Col, Empty, Grid } from 'antd';
import styled from 'styled-components';
import { RiFolderChartLine, RiSettings5Line } from '@remixicon/react';
import DetailItem from '../../../../components/DetailItem';

import useReportLog from '../../../../hooks/useReportLog';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { toHHMMSS } from '../helperFunction';

dayjs.extend(utc);

interface ClutchEvent {
  engine: string;
  status: string;
  startTimestamp: number;
  endTimestamp: number;
  duration: number;
  ongoing?: boolean;
}

interface TableEvent {
  key: string;
  startTimestamp: number;
  endTimestamp: number;
  engine: string;
  status: string;
  duration: number;
  startRowSpan?: number;
  endRowSpan?: number;
}

const TabClutchEvents = () => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;

  const { clutchEvents: eventsData } = useReportLog();
  const { timezone } = useAppliedReportFilter();
  const [selectedEngine, setSelectedEngine] = useState<string>('ALL');

  const tableEvents = useMemo<TableEvent[]>(() => {
    if (!eventsData?.events) return [];
    
    let filtered = eventsData.events;
    if (selectedEngine !== 'ALL') {
      filtered = filtered.filter((e: ClutchEvent) => e.engine === selectedEngine);
    }

    const sorted = [...filtered].sort((a, b) => {
      if (b.startTimestamp !== a.startTimestamp) {
        return b.startTimestamp - a.startTimestamp;
      }
      return a.engine.localeCompare(b.engine);
    });

    const events: TableEvent[] = sorted.map((event, index) => ({
      key: `${event.startTimestamp}-${event.endTimestamp}-${event.engine}-${index}`,
      startTimestamp: event.startTimestamp,
      endTimestamp: event.endTimestamp,
      engine: event.engine,
      status: event.status,
      duration: event.duration,
      startRowSpan: 1,
      endRowSpan: 1,
    }));

    for (let i = 0; i < events.length; i++) {
      if (events[i].startRowSpan === 0) continue;
      
      let span = 1;
      for (let j = i + 1; j < events.length; j++) {
        if (events[j].startTimestamp === events[i].startTimestamp) {
          events[j].startRowSpan = 0;
          span++;
        } else {
          break;
        }
      }
      events[i].startRowSpan = span;
    }

    for (let i = 0; i < events.length; i++) {
      if (events[i].endRowSpan === 0) continue;
      
      let span = 1;
      for (let j = i + 1; j < events.length; j++) {
        if (events[j].endTimestamp === events[i].endTimestamp) {
          events[j].endRowSpan = 0;
          span++;
        } else {
          break;
        }
      }
      events[i].endRowSpan = span;
    }

    return events;
  }, [eventsData, selectedEngine]);

  const columns = [
    {
      title: 'Start',
      dataIndex: 'startTimestamp',
      key: 'startTimestamp',
      width: isMobile ? 352 : undefined,
      render: (ts: number) =>
        dayjs(ts * 1000)
          .utcOffset(timezone * 60)
          .format(isMobile ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY HH:mm:ss'),
      onCell: (record: TableEvent) => ({
        rowSpan: record.startRowSpan,
      }),
    },
    {
      title: 'End',
      dataIndex: 'endTimestamp',
      key: 'endTimestamp',
      width: isMobile ? 352 : undefined,
      render: (ts: number) =>
        dayjs(ts * 1000)
          .utcOffset(timezone * 60)
          .format(isMobile ? 'DD MMM YYYY, HH:mm' : 'DD MMM YYYY HH:mm:ss'),
      onCell: (record: TableEvent) => ({
        rowSpan: record.endRowSpan,
      }),
    },
    {
      title: 'Clutch',
      dataIndex: 'engine',
      key: 'engine',
      width: isMobile ? 320 : undefined,
      render: (engine: string) => engine,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: isMobile ? 97 : undefined,
      render: (status: string) => {
        const isClutchOn = status === 'ON';

        return (
          <Tag
            style={
              isMobile
                ? {
                    fontWeight: 600,
                    color: isClutchOn ? '#73c241' : '#eb5b55',
                    backgroundColor: isClutchOn ? '#f8ffef' : '#fdf3f1',
                    border: `1px solid ${isClutchOn ? '#73c241' : '#eb5b55'}`,
                    borderRadius: 5,
                    lineHeight: 1.4,
                    fontSize: 12,
                    padding: '2px 8px',
                  }
                : {
                    fontWeight: 'bold',
                    color: isClutchOn ? '#1e9b24' : '#b81414',
                    backgroundColor: isClutchOn ? '#95f39a' : '#ffb4b4',
                    border: 'none',
                    lineHeight: 1.4,
                    fontSize: 12,
                  }
            }
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Running Hour',
      dataIndex: 'duration',
      key: 'duration',
      width: isMobile ? 140 : undefined,
      render: (duration: number) => toHHMMSS(duration),
    },
  ];

  const totalRunningHour = useMemo(() => {
    if (!eventsData) return 0;
    return (
      eventsData.summary.port.totalEngineOn +
      eventsData.summary.center.totalEngineOn +
      eventsData.summary.starboard.totalEngineOn
    );
  }, [eventsData]);

  const hasPort = eventsData && eventsData.summary.port.clutchCount > 0;
  const hasCenter = eventsData && eventsData.summary.center.clutchCount > 0;
  const hasStarboard = eventsData && eventsData.summary.starboard.clutchCount > 0;

  const engineCardsCount = [hasPort, hasCenter, hasStarboard].filter(Boolean).length;
  const summaryCardSpan = engineCardsCount === 0 ? 24 : 24 / engineCardsCount;

  return (
    <Container>
      {!eventsData || (eventsData.events.length === 0 && engineCardsCount === 0) ? (
        <EmptyStateContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <EmptyDescription>
                <span>Event Clutch is available for this asset, but no clutch event data is available.</span>
                <span>Clutch events are generated from clutch data when clutches are turned ON/OFF.</span>
              </EmptyDescription>
            }
          />
        </EmptyStateContainer>
      ) : (
        <>
            <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
              {hasPort && (
                <Col xs={24} sm={12} lg={summaryCardSpan}>
                  <DashboardCard
                    title={
                      <DashboardCardTitle>
                        {isMobile ? (
                          <RiFolderChartLine size={14} color="#333333" />
                        ) : (
                          <RiSettings5Line size={18} color="#b5b7b9" />
                        )}
                        Port Summary
                      </DashboardCardTitle>
                    }
                  >
                    <DetailItem label="AVG. PORT CLUTCH" value={eventsData.summary.port.avgClutch.toFixed(2)} valueFontSize={20} boldLabel valueUnit="CLUTCH" />
                    <DetailItem label="TOTAL PORT CLUTCH ON" value={toHHMMSS(eventsData.summary.port.totalEngineOn)} valueFontSize={20} boldLabel valueUnit="HH:MM:SS" />
                    <DetailItem label="TOTAL PORT CLUTCH OFF" value={toHHMMSS(eventsData.summary.port.totalEngineOff)} valueFontSize={20} boldLabel valueUnit="HH:MM:SS" />
                  </DashboardCard>
                </Col>
              )}
              {hasCenter && (
                <Col xs={24} sm={12} lg={summaryCardSpan}>
                  <DashboardCard
                    title={
                      <DashboardCardTitle>
                        {isMobile ? (
                          <RiFolderChartLine size={14} color="#333333" />
                        ) : (
                          <RiSettings5Line size={18} color="#b5b7b9" />
                        )}
                        Center Summary
                      </DashboardCardTitle>
                    }
                  >
                    <DetailItem label="AVG. CENTER CLUTCH" value={eventsData.summary.center.avgClutch.toFixed(2)} valueFontSize={20} boldLabel valueUnit="CLUTCH" />
                    <DetailItem label="TOTAL CENTER CLUTCH ON" value={toHHMMSS(eventsData.summary.center.totalEngineOn)} valueFontSize={20} boldLabel valueUnit="HH:MM:SS" />
                    <DetailItem label="TOTAL CENTER CLUTCH OFF" value={toHHMMSS(eventsData.summary.center.totalEngineOff)} valueFontSize={20} boldLabel valueUnit="HH:MM:SS" />
                  </DashboardCard>
                </Col>
              )}
              {hasStarboard && (
                <Col xs={24} sm={12} lg={summaryCardSpan}>
                  <DashboardCard
                    title={
                      <DashboardCardTitle>
                        {isMobile ? (
                          <RiFolderChartLine size={14} color="#333333" />
                        ) : (
                          <RiSettings5Line size={18} color="#b5b7b9" />
                        )}
                        Starboard Summary
                      </DashboardCardTitle>
                    }
                  >
                    <DetailItem label="AVG. STARBOARD CLUTCH" value={eventsData.summary.starboard.avgClutch.toFixed(2)} valueFontSize={20} boldLabel valueUnit="CLUTCH" />
                    <DetailItem label="TOTAL STARBOARD CLUTCH ON" value={toHHMMSS(eventsData.summary.starboard.totalEngineOn)} valueFontSize={20} boldLabel valueUnit="HH:MM:SS" />
                    <DetailItem label="TOTAL STARBOARD CLUTCH OFF" value={toHHMMSS(eventsData.summary.starboard.totalEngineOff)} valueFontSize={20} boldLabel valueUnit="HH:MM:SS" />
                  </DashboardCard>
                </Col>
              )}
            </Row>

            <FilterSection>
              <Select
                value={selectedEngine}
                onChange={setSelectedEngine}
                style={{ width: isMobile ? '100%' : 200 }}
                options={[
                  { label: 'All Clutches', value: 'ALL' },
                  { label: 'Port Clutch', value: 'PORT' },
                  { label: 'Starboard Clutch', value: 'STARBOARD' },
                  { label: 'Center Clutch', value: 'CENTER' },
                ]}
              />
              <RunningHourStat>
                <RiFolderChartLine size={20} color="#b5b7b9" />
                <StatLabel>Total Running Hour:</StatLabel>
                <StatValue>{toHHMMSS(totalRunningHour)}</StatValue>
                <StatUnit>HH:MM:SS</StatUnit>
              </RunningHourStat>
            </FilterSection>

            <TableFrame>
              <TableStyled
                columns={columns}
                dataSource={tableEvents}
                rowKey={(record) => record.key}
                size={isMobile ? 'small' : 'middle'}
                scroll={isMobile ? { x: 'max-content' } : undefined}
                pagination={{
                  pageSize: isMobile ? 5 : 20,
                  showSizeChanger: !isMobile,
                  showTotal: (total, range) =>
                    isMobile
                      ? `Showing ${range[0]} to ${range[1]} of ${total} results`
                      : `Total ${total} events`,
                }}
              />
            </TableFrame>
          </>
        )}
    </Container>
  );
};

const Container = styled.div`
  padding: 16px 0;

  @media (max-width: 767px) {
    padding: 12px 20px 0;

    .ant-row {
      margin-left: 0 !important;
      margin-right: 0 !important;
      row-gap: 12px !important;
    }

    .ant-col {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  background-color: #fafafa;
  border-radius: 8px;
  margin: 16px 0;
`;

const EmptyDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  color: #666;
`;

const DashboardCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 767px) {
    color: #333333;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.2;

    svg {
      width: 14px;
      height: 14px;
      color: #333333;
    }
  }
`;

const DashboardCard = styled(Card)`
  .ant-card-head {
    padding: 5px 10px !important;
    min-height: 20px !important;
    font-size: 14px;
  }
  .ant-card-body {
    padding: 5px 10px !important;
  }

  @media (max-width: 767px) {
    width: 100%;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    background: #ffffff;
    overflow: hidden;

    .ant-card-head {
      min-height: 34px !important;
      padding: 4px 12px 0 !important;
      border-bottom: 1px solid rgba(51, 51, 51, 0.2);
    }

    .ant-card-body {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 12px 12px 16px !important;
    }

    .ant-card-body > div {
      width: 100%;
      margin: 0 !important;
    }
  }
`;

const FilterSection = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 767px) {
    display: none;
  }
`;

const RunningHourStat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 767px) {
    justify-content: space-between;
    gap: 6px;
    padding: 10px 12px;
    border: 1px solid #e8ecf3;
    border-radius: 8px;
    background: #ffffff;
  }
`;

const StatLabel = styled.span`
  font-size: 14px;
  color: #768499;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`;

const StatUnit = styled.span`
  font-size: 12px;
  color: #768499;
`;

const TableStyled = styled(Table)`
  .ant-table-thead > tr > th {
    background: #fafafa;
    font-weight: 600;
    font-size: 13px;
    padding: 8px 12px;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 6px 12px;
  }

  @media (max-width: 767px) {
    .ant-table {
      min-width: 1261px;
    }

    .ant-table-container {
      border-start-start-radius: 0;
      border-start-end-radius: 0;
    }

    .ant-table-thead > tr > th {
      white-space: nowrap;
      height: 46px;
      background: #fafafa;
      border-bottom: 1px solid #d9d9d9;
      color: #1e1e1e;
      font-size: 14px;
      padding: 8px 10px;
      letter-spacing: 0;
    }

    .ant-table-tbody > tr > td {
      white-space: nowrap;
      font-size: 12px;
      padding: 16px 12px;
      border-bottom: 1px solid #e8ecf3;
    }

    .ant-pagination {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin: 12px 0 0;
      gap: 6px;
    }

    .ant-pagination-total-text {
      flex: 1 1 auto;
      margin-inline-end: 0;
      color: #9facbf;
      font-size: 12px;
      line-height: 1.2;
      white-space: nowrap;
    }

    .ant-pagination-prev,
    .ant-pagination-next,
    .ant-pagination-item {
      min-width: 32px;
      height: 32px;
      margin-inline-end: 0;
      border-radius: 4px;
      line-height: 30px;
    }

    .ant-pagination-item-active {
      border-color: #272a35;
    }

    .ant-pagination-item-active a {
      color: #272a35;
    }
  }
` as typeof Table;

const TableFrame = styled.div`
  @media (max-width: 767px) {
    width: 100%;
    margin-top: 12px;
    overflow-x: auto;
    overflow-y: hidden;
    background: transparent;

    .ant-table-wrapper {
      min-width: 100%;
    }
  }
`;

export default TabClutchEvents;
